import {
  RUNTIME_TO_TOOL_ID,
  TOOL_ID_TO_RUNTIME,
  canActivateTool,
  getShapeFlyoutDefs,
  getToolById,
  getTypeFlyoutDefs,
  isShapeRuntimeTool,
  isTypeRuntimeTool,
  type ToolId,
} from '@canvas/shared-types';
import { useCanvasStore, type CanvasTool } from '@/stores/canvasStore';
import { TOOL_ICONS } from '@/lib/toolIcons';

/** Primary tools shown in the phone bottom dock (scrollable). */
const PRIMARY_TOOL_IDS: ToolId[] = [
  'move',
  'lasso',
  'brush',
  'pencil',
  'eraser',
  'pen',
  'shape-rect',
  'type-point',
  'hand',
  'zoom',
];

const MORE_TOOL_IDS: ToolId[] = [
  'marquee-rect',
  'frame',
  'gradient',
  'paint-bucket',
  'eyedropper',
  'crop',
];

const SHAPE_TOOLS = getShapeFlyoutDefs().filter(canActivateTool);
const TYPE_TOOLS = getTypeFlyoutDefs().filter(canActivateTool);

interface MobileToolDockProps {
  onOpenLibraries: () => void;
  onOpenUploads: () => void;
  onOpenInspector: () => void;
  onOpenMore: () => void;
  onOpenShapes: () => void;
  onOpenType: () => void;
}

export default function MobileToolDock({
  onOpenLibraries,
  onOpenUploads,
  onOpenInspector,
  onOpenMore,
  onOpenShapes,
  onOpenType,
}: MobileToolDockProps) {
  const { selectedTool, lastShapeTool, lastTypeTool, setTool, undo, redo } =
    useCanvasStore();

  const shapeActive = isShapeRuntimeTool(selectedTool);
  const typeActive = isTypeRuntimeTool(selectedTool);
  const activeShapeId =
    RUNTIME_TO_TOOL_ID[shapeActive ? selectedTool : lastShapeTool];
  const activeTypeId = RUNTIME_TO_TOOL_ID[typeActive ? selectedTool : lastTypeTool];

  return (
    <nav className="ps-mobile-dock ps-mobile-tools-plate" aria-label="Tools">
      <div className="ps-mobile-dock-scroll">
        {PRIMARY_TOOL_IDS.map((id) => {
          if (id === 'shape-rect') {
            return (
              <button
                key={id}
                type="button"
                className={`ps-mobile-tool ${shapeActive ? 'active' : ''}`}
                title="Shapes"
                aria-pressed={shapeActive}
                onClick={() => {
                  setTool(lastShapeTool);
                  onOpenShapes();
                }}
              >
                {TOOL_ICONS[activeShapeId] ?? TOOL_ICONS['shape-rect']}
                <span>Shape</span>
              </button>
            );
          }
          if (id === 'type-point') {
            return (
              <button
                key={id}
                type="button"
                className={`ps-mobile-tool ${typeActive ? 'active' : ''}`}
                title="Type"
                aria-pressed={typeActive}
                onClick={() => {
                  setTool(lastTypeTool);
                  onOpenType();
                }}
              >
                {TOOL_ICONS[activeTypeId] ?? TOOL_ICONS['type-point']}
                <span>Type</span>
              </button>
            );
          }

          const def = getToolById(id);
          const runtime = TOOL_ID_TO_RUNTIME[id] as CanvasTool | undefined;
          if (!def || !runtime || !canActivateTool(def)) return null;
          const active = selectedTool === runtime;
          return (
            <button
              key={id}
              type="button"
              className={`ps-mobile-tool ${active ? 'active' : ''}`}
              title={def.label}
              aria-pressed={active}
              onClick={() => setTool(runtime)}
            >
              {TOOL_ICONS[id]}
              <span>{def.label.split(' ')[0]}</span>
            </button>
          );
        })}

        <button
          type="button"
          className="ps-mobile-tool"
          title="More tools"
          onClick={onOpenMore}
        >
          <svg viewBox="0 0 24 24" aria-hidden>
            <circle cx="5" cy="12" r="1.6" fill="currentColor" stroke="none" />
            <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
            <circle cx="19" cy="12" r="1.6" fill="currentColor" stroke="none" />
          </svg>
          <span>More</span>
        </button>
      </div>

      <div className="ps-mobile-dock-actions">
        <button type="button" className="ps-mobile-action" onClick={onOpenLibraries}>
          Libraries
        </button>
        <button type="button" className="ps-mobile-action" onClick={onOpenUploads}>
          Upload
        </button>
        <button type="button" className="ps-mobile-action" onClick={onOpenInspector}>
          Layers
        </button>
        <button
          type="button"
          className="ps-mobile-action icon"
          title="Undo"
          onClick={() => undo()}
        >
          <svg viewBox="0 0 24 24">
            <path d="M9 7H4v5M4 7c3 0 7 1 9 5 1 2 1 5 0 7" />
          </svg>
        </button>
        <button
          type="button"
          className="ps-mobile-action icon"
          title="Redo"
          onClick={() => redo()}
        >
          <svg viewBox="0 0 24 24">
            <path d="M15 7h5v5M20 7c-3 0-7 1-9 5-1 2-1 5 0 7" />
          </svg>
        </button>
      </div>
    </nav>
  );
}

export function MobileMoreToolsGrid({ onPicked }: { onPicked: () => void }) {
  const { selectedTool, setTool } = useCanvasStore();
  return (
    <div className="ps-mobile-tool-grid">
      {MORE_TOOL_IDS.map((id) => {
        const def = getToolById(id);
        const runtime = TOOL_ID_TO_RUNTIME[id] as CanvasTool | undefined;
        if (!def || !runtime || !canActivateTool(def)) return null;
        return (
          <button
            key={id}
            type="button"
            className={`ps-mobile-tool-tile ${selectedTool === runtime ? 'active' : ''}`}
            onClick={() => {
              setTool(runtime);
              onPicked();
            }}
          >
            {TOOL_ICONS[id]}
            <span>{def.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function MobileShapePicker({ onPicked }: { onPicked: () => void }) {
  const { selectedTool, setTool } = useCanvasStore();
  return (
    <div className="ps-mobile-tool-grid">
      {SHAPE_TOOLS.map((shape) => {
        const runtime = TOOL_ID_TO_RUNTIME[shape.id] as CanvasTool | undefined;
        if (!runtime) return null;
        return (
          <button
            key={shape.id}
            type="button"
            className={`ps-mobile-tool-tile ${selectedTool === runtime ? 'active' : ''}`}
            onClick={() => {
              setTool(runtime);
              onPicked();
            }}
          >
            {TOOL_ICONS[shape.id]}
            <span>{shape.label}</span>
          </button>
        );
      })}
    </div>
  );
}

export function MobileTypePicker({ onPicked }: { onPicked: () => void }) {
  const { selectedTool, setTool } = useCanvasStore();
  return (
    <div className="ps-mobile-tool-grid">
      {TYPE_TOOLS.map((typeDef) => {
        const runtime = TOOL_ID_TO_RUNTIME[typeDef.id] as CanvasTool | undefined;
        if (!runtime) return null;
        return (
          <button
            key={typeDef.id}
            type="button"
            className={`ps-mobile-tool-tile ${selectedTool === runtime ? 'active' : ''}`}
            onClick={() => {
              setTool(runtime);
              onPicked();
            }}
          >
            {TOOL_ICONS[typeDef.id]}
            <span>{typeDef.label}</span>
          </button>
        );
      })}
    </div>
  );
}
